import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { StateMessageComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  AttachmentGroupInfo,
  AttachmentSectionInfo,
  AzureBlobUploadResult,
  FileParameter,
} from '../../../sdk/sdk';
import { AttachmentService } from '../../../services/attachment.service';
import { FilePreviewService } from '../../../services/file-preview.service';
import { ModalService } from '../../../services/modal.service';

// Unused today — nothing imports this interface. Left exactly as-is rather than
// extended, so the diff does not imply it is on the upload path.
export interface AttachmentUploadDetailsResult {
  files: File[];
  groupId: string;
  description: string;
}

@Component({
  selector: 'app-attachment-upload-details-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, StateMessageComponent],
  templateUrl: './attachment-upload-details-modal.component.html',
})
export class AttachmentUploadDetailsModalComponent implements OnDestroy {
  private readonly attachmentService = inject(AttachmentService);
  private readonly modalService = inject(ModalService);
  private readonly filePreviewService = inject(FilePreviewService);
  readonly activeModal = inject(NgbActiveModal);
  private readonly previewUrls = new Set<string>();

  files: File[] = [];
  opportunityId = '';

  readonly uploading = signal(false);

  readonly sections = signal<AttachmentSectionInfo[]>([]);
  readonly loadingSections = signal(false);
  readonly loadSectionsError = signal<string | null>(null);

  readonly allGroups = signal<AttachmentGroupInfo[]>([]);
  readonly loadingGroups = signal(false);
  readonly loadGroupsError = signal<string | null>(null);

  readonly uploadForm = new FormGroup({
    sectionId: new FormControl<string | null>(null, Validators.required),
    groupId: new FormControl<string | null>({ value: null, disabled: true }),
    description: new FormControl<string>(''),
  });

  readonly selectedSectionId = toSignal(this.uploadForm.controls.sectionId.valueChanges, {
    initialValue: null,
  });

  readonly sectionGroups = computed(() => {
    const sectionId = this.selectedSectionId();
    if (!sectionId) return [];
    return this.allGroups().filter((group) => group.attachmentSectionId === sectionId);
  });

  constructor() {
    this.fetchAttachmentSections();
    this.fetchAttachmentGroups();

    this.uploadForm.controls.sectionId.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((sectionId) => this.onSectionChanged(sectionId));
  }

  fetchAttachmentSections(): void {
    this.loadingSections.set(true);
    this.loadSectionsError.set(null);
    this.attachmentService.getAttachmentSections().subscribe({
      next: (sections) => {
        this.sections.set(sections ?? []);
        this.loadingSections.set(false);
      },
      error: () => {
        this.loadSectionsError.set('Unable to load attachment sections.');
        this.loadingSections.set(false);
      },
    });
  }

  fetchAttachmentGroups(): void {
    this.loadingGroups.set(true);
    this.loadGroupsError.set(null);
    this.attachmentService.getAllAttachmentGroups().subscribe({
      next: (groups) => {
        this.allGroups.set(groups ?? []);
        this.loadingGroups.set(false);
      },
      error: () => {
        this.loadGroupsError.set('Unable to load attachment groups.');
        this.loadingGroups.set(false);
      },
    });
  }

  private onSectionChanged(sectionId: string | null): void {
    const groupControl = this.uploadForm.controls.groupId;
    groupControl.setValue(null, { emitEvent: false });

    if (sectionId) {
      groupControl.enable({ emitEvent: false });
    } else {
      groupControl.disable({ emitEvent: false });
    }
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onUpload(): void {
    if (this.uploadForm.invalid) {
      this.uploadForm.markAllAsTouched();
      return;
    }

    this.uploading.set(true);

    const { sectionId, groupId, description } = this.uploadForm.getRawValue();
    const resolvedDescription = description ?? '';

    const uploads = this.files.map((file) => {
      const fileParameter: FileParameter = {
        data: file,
        fileName: file.name,
      };

      return this.attachmentService
        .uploadAttachment(
          this.opportunityId,
          fileParameter,
          groupId ?? null,
          resolvedDescription,
          sectionId
        )
        .pipe(
          catchError((error) => {
            console.error(`Failed to upload file ${file.name}:`, error);
            return of(null);
          })
        );
    });

    forkJoin(uploads).subscribe({
      next: (results: (AzureBlobUploadResult[] | null)[]) => {
        this.uploading.set(false);
        const failed: File[] = [];
        for (let index = 0; index < results.length; index++) {
          const result = results[index];
          if (result === null) {
            failed.push(this.files[index]);
          }
        }

        if (failed.length > 0) {
          const failureModal = this.modalService.openAttachmentUploadFailureModal(
            failed,
            this.opportunityId,
            groupId ?? null,
            resolvedDescription,
            sectionId
          );
          failureModal.result
            .then((_result) => {
              this.activeModal.close({ reload: true });
            })
            .catch(() => {
              this.activeModal.close({ reload: true });
            });
        } else {
          this.activeModal.close();
          this.modalService.openAttachmentUploadSuccessModal();
        }
      },
      error: (error) => {
        this.uploading.set(false);
        console.error('Upload error:', error);

        const failureModal = this.modalService.openAttachmentUploadFailureModal(
          this.files,
          this.opportunityId,
          groupId ?? null,
          resolvedDescription,
          sectionId
        );
        failureModal.result
          .then((_result) => {
            this.activeModal.close({ reload: true });
          })
          .catch(() => {
            this.activeModal.close({ reload: true });
          });
      },
    });
  }

  ngOnDestroy(): void {
    this.previewUrls.forEach((url) => this.filePreviewService.revokePreviewUrl(url));
    this.previewUrls.clear();
  }
}
